import * as $ from 'jquery';
import { IDisposable, IEventAggregator, disposableSubscription } from 'common/barrels/core';
import { Observable, AsyncSubject, ISubscription } from 'common/barrels/rx';
import { IResourceProvider } from 'common/resourceprovider';
import { Serializer } from 'common/serializer';
import { Request } from 'common/request';
import { SystemError } from 'common/exceptions/systemerror';
import { dateTimeTransmissionFormat } from 'common/functions/date';

import { ViewScreen, ViewScreenType, ViewResultType, PageChangeSource, IViewLoadPageArgs } from 'runtime/screens/viewscreen';
import { WorkTrayView } from 'runtime/data/worktrayview';
import { NotBusy } from 'runtime/events/notbusy';
import { Control, IViewModelRoot } from 'runtime/controls/control';
import { NumericBox } from 'runtime/controls/numericbox';
import { WorkItemInfo } from 'runtime/data/workiteminfo';
import { WorkflowInstanceInfo } from 'runtime/data/workflowinstanceinfo';
import { WorkflowInstanceInfoPage } from 'runtime/data/workflowinstanceinfopage';

import * as path from 'shared/path';

import * as ko from 'knockout';
import * as komapping from 'knockout.mapping';
import * as rxui from 'common/rxui/core';
import * as moment from 'moment'
import { List } from 'immutable';

import 'runtime/styles/screens/workflowstatus.less';

interface IStatusQuery {
    includeCreatedByCurrentUser: boolean;
    includeSubmittedByCurrentUser: boolean;
    includeAssignedByCurrentUser: boolean;
    includeTransferredByCurrentUser: boolean;
    includeCurrentlyClaimedByCurrentUser: boolean;
    includeCurrentlyAvailable: boolean;

    includeInProgress: boolean;
    includeCompleted: boolean;

    fromDate: string;
    toDate: string;

    titleFilter: string;
    definitionFilter: string;
    stepFilter: string;
}

class SearchEvent {
    eventName = SearchEvent.eventName;
    static eventName() { return 'WorkFlowStatus.Search'; }
}

export class WorkflowStatus extends ViewScreen {
    private vm: WorkflowStatusViewModel;
    private workflowStatusDisposable: IDisposable;

    constructor($hostElement: JQuery, resourcesObservable: Observable<IResourceProvider>, parentEventAggregator: IEventAggregator, serializer: Serializer, view: string, csrfToken: () => string) {
        super($hostElement, resourcesObservable, parentEventAggregator, ViewScreenType.WorkflowStatus, serializer, csrfToken);

        var asyncSubject = new AsyncSubject<WorkTrayView>();
        var asyncSubjectSubscription: ISubscription = null;
        asyncSubjectSubscription = Observable.zip(this.viewAvailable, asyncSubject, (v1, v2) => v2).subscribe(value => this.initializeWorkflowStatus(value, disposableSubscription(asyncSubject, asyncSubjectSubscription)));

        Request.create(serializer, path.api(`workflowstatus/${view}`))
            .handle(WorkTrayView, value => {
                asyncSubject.next(value);
                asyncSubject.complete();
            })
            .handle(SystemError, () => this.systemError('WorkflowStatus screen failed to load: Internal System Error'))
            .error((jqXhr, textStatus, errorThrown) => this.httpError(`WorkflowStatus screen failed to load: ${errorThrown}`, textStatus))
            .final(() => this.eventAggregator.publish(new NotBusy()))
            .get();
    }

    dispose(): void {
        this.workflowStatusDisposable.dispose();
    }

    remove(): void {
        this.$element.remove();
        this.dispose();
    }

    private initializeWorkflowStatus(view: WorkTrayView, constructorDisposable: IDisposable): void {
        this.viewId.next(view.id);
        this.view.next(view);

        this.buildWorkflowStatusContainer(this.$element, this.resources);

        Control.instance($('.wf-page-index .numericbox')[0])['content.Value'](1);
        Control.instance($('.wf-page-index .numericbox')[0])['content.Value'](0);
        var pageSizeControl = <NumericBox>Control.instance($('.wf-page-size .numericbox')[0]);
        pageSizeControl['content.Value'](0);
        pageSizeControl['content.Value'](this.defaultPageSize);
        pageSizeControl.setAdditionalValidator((raw, dec) => raw !== null && dec.greaterThan(0));

        var vm = this.vm = new WorkflowStatusViewModel(this.eventAggregator, this.resources);

        ko.applyBindings(vm, this.$element[0]);

        this.paintViewHeader(view, ViewResultType.WorkflowStatus);

        this.workflowStatusDisposable = disposableSubscription(
            this.viewScreenDisposable,
            constructorDisposable,

            this.eventAggregator.query(SearchEvent).subscribe(() => this.performSearch()),

            this.subscribeToItemActions(),
            this.subscribeToSubjects()
        );

        this.viewInitialized = true;
    }

    private buildWorkflowStatusContainer($element: JQuery, resources: IResourceProvider): void {
        const options = List([
            { row: 0, col: 0, prop: 'includeCreatedByCurrentUser', label: resources.getCoreString('XamlCreatedByMe') },
            { row: 1, col: 0, prop: 'includeSubmittedByCurrentUser', label: resources.getCoreString('XamlSubmittedByMe') },
            { row: 2, col: 0, prop: 'includeCurrentlyClaimedByCurrentUser', label: resources.getCoreString('XamlCurrentlyClaimedByMe') },

            { row: 0, col: 1, prop: 'includeCurrentlyAvailable', label: resources.getCoreString('XamlCurrentlyAvailableForClaim') },
            { row: 1, col: 1, prop: 'includeAssignedByCurrentUser', label: resources.getCoreString('XamlAssignedToAnotherUserByMe') },
            { row: 2, col: 1, prop: 'includeTransferredByCurrentUser', label: resources.getCoreString('XamlTransferredToAnotherUserByMe') }
        ]).groupBy(x => x.col);

        const states = [
            { col: 0, prop: 'includeInProgress', label: resources.getCoreString('XamlInProgress') },
            { col: 0, prop: 'includeCompleted', label: resources.getCoreString('WorkItemStateComplete') }
        ];
        
        $('.wf-header', this.$element).append([
            $('<div />').addClass('wf-workflowstatus-filter-table').append([
                options.reduce<JQuery>((p, c) =>
                    p.append(
                        c.reduce<JQuery>((cp, cc) =>
                            cp.append(
                                $('<label />').append([
                                    $('<input />').attr('type', 'checkbox').attr('data-bind', `checked: ${cc.prop}`),
                                    cc.label
                                ])
                            )
                            , $('<div />').addClass('wf-filter-control wf-checkbox'))
                    )
                    , $('<div />').addClass('wf-filter').append($('<div />').addClass('wf-filter-label').text(resources.getCoreString('XamlOptions')))),
                states.reduce<JQuery>((p, c) =>
                    p.append(
                        $('<div />').addClass('wf-filter-control wf-checkbox').append(
                            $('<label />').append([
                                $('<input />').attr('type', 'checkbox').attr('data-bind', `checked: ${c.prop}`),
                                c.label
                            ])
                        )
                    )
                    , $('<div />').addClass('wf-filter').append($('<div />').addClass('wf-filter-label').text(resources.getCoreString('XamlState'))))
            ]),
            $('<div />').addClass('wf-filter').append([
                $('<div />').addClass('wf-filter-label').text(resources.getCoreString('XamlDateRange')),
                $('<div />').addClass('wf-filter-control').append([
                    $('<div />').addClass('wf-workflowstatus-date').append(
                        //$('<div />').attr('data-bind', 'dateTimePicker: { selectedDate: fromDate, options: { elementWidth: \'115px\' } }')
                        $('<div />').attr('data-bind', 'dateTimePicker: { selectedDate: fromDate }')
                    ),
                    $('<div />').addClass('wf-filter-text').text(resources.getCoreString('XamlTo')),
                    $('<div />').addClass('wf-workflowstatus-date').append(
                        $('<div />').attr('data-bind', 'dateTimePicker: { selectedDate: toDate }')
                    )
                ])
            ]),
            $('<div />').addClass('wf-filter').append([
                $('<div />').addClass('wf-filter-label').text(resources.getCoreString('XamlTitle')),
                $('<div />').addClass('wf-filter-control').append(
                    $('<div />').addClass('wf-workflowstatus-textbox').append([
                        $('<div />').attr('data-bind', 'textBox: { value: titleFilter }')
                    ])
                )
            ]),
            $('<div />').addClass('wf-filter').append([
                $('<div />').addClass('wf-filter-label').text(resources.getCoreString('XamlDefinitionType')),
                $('<div />').addClass('wf-filter-control').append(
                    $('<div />').addClass('wf-workflowstatus-textbox').append([
                        $('<div />').attr('data-bind', 'textBox: { value: definitionFilter }')
                    ])
                )
            ]),
            $('<div />').addClass('wf-filter').append([
                $('<div />').addClass('wf-filter-label').text(resources.getCoreString('XamlCurrentStep')),
                $('<div />').addClass('wf-filter-control').append([
                    $('<div />').addClass('wf-workflowstatus-textbox').append([
                        $('<div />').attr('data-bind', 'textBox: { value: stepFilter }')
                    ])
                ])
            ]),
            $('<div />').addClass('wf-filter').append([
                $('<div />').addClass('wf-filter-reset').append([
                    $('<div />').attr('data-bind', `activityButton: { click: reset, clickBubble: false, text: \'${resources.getCoreString('XamlReset')}\' }`)
                ]),
                $('<div />').addClass('wf-filter-search').append([
                    $('<div />').attr('data-bind', `activityButton: { click: search, clickBubble: false, text: \'${resources.getCoreString('XamlSearch')}\' }`)
                ])
            ])
        ]);
    }

    private subscribeToItemActions(): ISubscription {
        var $table = $('.wf-view-table-inner table', this.$element);

        return rxui.click($table).subscribe(mouseEvent => {
            var $target = $(mouseEvent.target);

            var $menuItem = $target.closest('.wf-item-menu-item');

            if ($menuItem === undefined || $menuItem === null || $menuItem.length === 0) {
                return;
            }

            mouseEvent.stopPropagation();

            var action = $menuItem.data('itemAction');

            var $tr = $target.closest('tr');

            switch (action) {

                case 'showHistory':
                    var item: WorkflowInstanceInfo = $tr.data('key');
                    this.showHistory($tr, item.workflowInstanceId);
                    $menuItem.find('i').removeClass('fa-plus').addClass('fa-minus');
                    $menuItem.data('itemAction', 'hideHistory');
                    break;

                case 'hideHistory':
                    $menuItem.data('itemAction', 'showHistory');
                    $menuItem.find('i').removeClass('fa-minus').addClass('fa-plus');
                    var $historyTr = $tr.next('.wf-item-history');
                    $historyTr.next('.wf-item-history').remove();
                    $historyTr.remove();
                    break;

                case 'edit':
                    var editWorkItem: WorkItemInfo = $tr.data('key');
                    this.openEdit(editWorkItem.workItemId);
                    break;

                case 'view':
                    var viewWorkItem: WorkItemInfo = $tr.data('key');
                    this.openRead(viewWorkItem.workItemId, this.view.getValue().viewDataPreference);
                    break;
            }
        });
    }

    private subscribeToSubjects(): ISubscription {
        return Observable.merge<PageChangeSource>(this.pageSizeSource, this.pageIndexSource)
            .skipWhile(() => !this.viewInitialized)
            .filter(() => !this.viewUpdating)
            .subscribe(
            value => {
                this.updating();

                var args: IViewLoadPageArgs = {
                    viewId: this.view.getValue().id,
                    pageSize: value.pageSize || this.pageSize.getValue(),
                    pageIndex: value.pageIndex || this.pageIndex.getValue(),
                    sortDescriptions: this.sortDescriptions.getValue()
                };

                this.loadPage(args, () => this.notUpdating());
            },
            // this should never execute as the subscriptions are to behavior subjects of primitive types
            (exception: any) => {
                console.log(JSON.stringify(exception));
                alert('ERROR.  Check console');
            }
        );
    }

    private loadPage(args: IViewLoadPageArgs, final: () => void): void {
        var requestArgs: any = {
            pageSize: args.pageSize,
            pageIndex: Math.max(0, args.pageIndex)
        };
        if (args.sortDescriptions !== undefined && args.sortDescriptions !== null && args.sortDescriptions.length > 0) {
            requestArgs.sortDescriptions = args.sortDescriptions;
        }
        requestArgs.statusQuery = this.vm.getStatusQuery();

        Request.create(this.serializer, path.api(`workflowstatus/page/${args.viewId}`))
            .args(() => requestArgs)
            .handle(WorkflowInstanceInfoPage, page => this.page.next(page))
            .handle(SystemError, t => this.systemError(t))
            .error((jqXhr, textStatus, errorThrown) => this.httpError(errorThrown, textStatus))
            .final(final)
            .post(this.csrfToken());
    }

    private showHistory($target: JQuery, workflowInstanceId: string): void {
        Request.create(this.serializer, path.api(`workflowstatus/history/${workflowInstanceId}`))
            .before(() => this.updating())
            .handleArray(WorkItemInfo, history => this.showWorkflowStatusHistory($target, history))
            .handle(null, history => this.showWorkflowStatusHistory($target, []))
            .handle(SystemError, () => this.systemError('System Error : WorkflowStatus.showHistory.get'))
            .error((jqXhr, textStatus, errorThrown) => this.httpError(`Http Error : WorkflowStatus.showHistory.get : ${errorThrown}`, textStatus))
            .final(() => this.notUpdating())
            .post(this.csrfToken());
    }

    private performSearch(): void {
        this.updating();

        var args: IViewLoadPageArgs = {
            viewId: this.view.getValue().id,
            pageSize: this.pageSize.getValue(),
            pageIndex: this.pageIndex.getValue(),
            sortDescriptions: this.sortDescriptions.getValue()
        };
        this.loadPage(args, () => this.notUpdating());
    }

    protected onViewChanged(view: WorkTrayView, final: () => void): void {
        this.paintViewHeader(view, ViewResultType.WorkflowStatus);

        var args: IViewLoadPageArgs = {
            viewId: view.id,
            pageSize: this.pageSize.getValue(),
            pageIndex: this.firstPageIndex,
            sortDescriptions: this.sortDescriptions.getValue()
        };

        this.loadPage(args, final);
    }

    protected reloadPage(args: IViewLoadPageArgs, final: () => void): void {
        this.loadPage(args, final);
    }

    protected paintViewBodyVirtual(view: WorkTrayView, page: WorkflowInstanceInfoPage): void {
        //if (page !== null && page instanceof core.WorkflowInstanceInfoPage) {
        this.paintViewBody(true /*displayHistory*/, false /*displayActionMenu*/, page);
        //}
    }
}

const dateValueFormat = dateTimeTransmissionFormat();

class WorkflowStatusViewModel implements IViewModelRoot {
    eventAggregator: IEventAggregator;
    resources: IResourceProvider;

    includeCreatedByCurrentUser = ko.observable<boolean>();
    includeSubmittedByCurrentUser = ko.observable<boolean>();
    includeAssignedByCurrentUser = ko.observable<boolean>();
    includeTransferredByCurrentUser = ko.observable<boolean>();
    includeCurrentlyClaimedByCurrentUser = ko.observable<boolean>();
    includeCurrentlyAvailable = ko.observable<boolean>();

    includeInProgress = ko.observable<boolean>();
    includeCompleted = ko.observable<boolean>();

    fromDate = ko.observable<string>();
    toDate = ko.observable<string>();

    titleFilter = ko.observable<string>();
    definitionFilter = ko.observable<string>();
    stepFilter = ko.observable<string>();

    constructor(eventAggregator: IEventAggregator, resources: IResourceProvider) {
        this.eventAggregator = eventAggregator;
        this.resources = resources;

        this.reset();

        this.fromDate.subscribe(newValue => {
            if (newValue === undefined || newValue === null) {
                this.resetFromDate();
            }
        });

        this.toDate.subscribe(newValue => {
            if (newValue === undefined || newValue === null) {
                this.resetToDate();
            }
        });
    }

    private resetFromDate(): void {
        var fromDateValue = moment().startOf('day').add(-3, 'months').format(dateValueFormat);
        this.fromDate(fromDateValue);
    }

    private resetToDate(): void {
        var toDateValue = moment().endOf('day').format(dateValueFormat);
        this.toDate(toDateValue);
    }

    search(): void {
        this.eventAggregator.publish(new SearchEvent());
    }

    reset(): void {
        this.includeCreatedByCurrentUser(true);
        this.includeSubmittedByCurrentUser(false);
        this.includeCurrentlyClaimedByCurrentUser(false);
        this.includeCurrentlyAvailable(false);
        this.includeAssignedByCurrentUser(false);
        this.includeTransferredByCurrentUser(false);

        this.includeInProgress(true);
        this.includeCompleted(true);

        this.resetFromDate();
        this.resetToDate();

        this.titleFilter(null);
        this.definitionFilter(null);
        this.stepFilter(null);
    }

    getStatusQuery(): IStatusQuery {
        return <any>komapping.toJS(this, { ignore: ['dateValueFormat', 'eventAggregator', 'resources', 'resetFromDate', 'resetToDate', 'search', 'reset', 'getStatusQuery'] });
    }
}